import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Divider,
} from '@mui/material';
import {
  Assignment,
  CheckCircle,
  Cancel,
  Visibility,
  Description,
  Business,
  Phone,
  Email,
  Language,
  LocationOn,
  Refresh,
} from '@mui/icons-material';
import axios from 'axios';
import firebase from 'firebase/app';
import TabDescription from './AdminDashboard/TabDescription';
import { useRegionContext } from '../region/RegionContext';
import 'firebase/auth';

const PAGE_SIZE = 25;

const AdminClaimReview = () => {
  const { endpoints } = useRegionContext();
  const [currentTab, setCurrentTab] = useState(0);
  const [pendingClaims, setPendingClaims] = useState([]);
  const [approvedClaims, setApprovedClaims] = useState([]);
  const [rejectedClaims, setRejectedClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [notes, setNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [claimPage, setClaimPage] = useState(0);

  useEffect(() => {
    document.title = "Claim Reviews | Piatto";
  }, []);

  useEffect(() => {
    loadClaimReviews();
  }, []);

  const loadClaimReviews = async () => {
    try {
      setLoading(true);
      setError(null);

      const user = firebase.auth().currentUser;
      if (!user) {
        setError('You must be logged in as an admin to view this page');
        return;
      }

      const idToken = await user.getIdToken();
      const headers = { Authorization: `Bearer ${idToken}` };

      // Fetch all three statuses in parallel
      const [pendingRes, approvedRes, rejectedRes] = await Promise.allSettled([
        axios.get(`${endpoints.getAdminReviewQueue}?status=pending&limit=100`, { headers }),
        axios.get(`${endpoints.getAdminReviewQueue}?status=approved&limit=100`, { headers }),
        axios.get(`${endpoints.getAdminReviewQueue}?status=rejected&limit=100`, { headers }),
      ]);

      setPendingClaims(
        pendingRes.status === 'fulfilled' ? pendingRes.value.data.reviews || [] : []
      );
      setApprovedClaims(
        approvedRes.status === 'fulfilled' ? approvedRes.value.data.reviews || [] : []
      );
      setRejectedClaims(
        rejectedRes.status === 'fulfilled' ? rejectedRes.value.data.reviews || [] : []
      );
    } catch (err) {
      console.error('Error loading claims:', err);
      setError(
        err.response?.data?.error ||
          'Failed to load claim reviews. Please ensure you are logged in as an admin.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOpenReview = (claim) => {
    setSelectedClaim(claim);
    setNotes('');
    setRejectionReason('');
    setReviewDialogOpen(true);
  };

  const handleCloseReview = () => {
    setReviewDialogOpen(false);
    setSelectedClaim(null);
    setNotes('');
    setRejectionReason('');
  };

  const handleApproveClaim = async () => {
    if (!selectedClaim) return;

    try {
      setActionLoading(true);

      const user = firebase.auth().currentUser;
      const idToken = await user.getIdToken();

      await axios.post(
        endpoints.approveVenueClaim,
        {
          reviewId: selectedClaim.reviewId,
          sessionId: selectedClaim.sessionId,
          notes: notes || 'Document verified and approved',
        },
        { headers: { Authorization: `Bearer ${idToken}` } }
      );

      handleCloseReview();
      loadClaimReviews();
    } catch (err) {
      console.error('Error approving claim:', err);
      alert(err.response?.data?.error || 'Failed to approve claim. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectClaim = async () => {
    if (!selectedClaim) return;

    if (!rejectionReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }

    try {
      setActionLoading(true);

      const user = firebase.auth().currentUser;
      const idToken = await user.getIdToken();

      await axios.post(
        endpoints.rejectVenueClaim,
        {
          reviewId: selectedClaim.reviewId,
          sessionId: selectedClaim.sessionId,
          reason: rejectionReason,
          notes: notes || '',
        },
        { headers: { Authorization: `Bearer ${idToken}` } }
      );

      handleCloseReview();
      loadClaimReviews();
    } catch (err) {
      console.error('Error rejecting claim:', err);
      alert(err.response?.data?.error || 'Failed to reject claim. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const ms = timestamp._seconds ? timestamp._seconds * 1000 : timestamp;
    return new Date(ms).toLocaleString('en-AU');
  };

  const ClaimTable = ({ claims, showActions = false }) => (
    <>
    {claims.length > PAGE_SIZE && (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 2 }}>
        <Button variant="outlined" size="small" disabled={claimPage === 0} onClick={() => setClaimPage(p => p - 1)}>
          Previous
        </Button>
        <Typography variant="body2">
          Page {claimPage + 1} of {Math.ceil(claims.length / PAGE_SIZE)} ({claims.length} total)
        </Typography>
        <Button variant="outlined" size="small" disabled={(claimPage + 1) * PAGE_SIZE >= claims.length} onClick={() => setClaimPage(p => p + 1)}>
          Next
        </Button>
      </Box>
    )}
    <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table aria-label="Venue claim reviews">
        <TableHead>
          <TableRow>
            <TableCell scope="col">Venue</TableCell>
            <TableCell scope="col">Document Type</TableCell>
            <TableCell scope="col">Submitted</TableCell>
            <TableCell scope="col">Status</TableCell>
            {showActions && <TableCell scope="col" align="center">Actions</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {claims.length === 0 ? (
            <TableRow>
              <TableCell colSpan={showActions ? 5 : 4} align="center">
                <Typography color="text.secondary" sx={{ py: 3 }}>No claims found</Typography>
              </TableCell>
            </TableRow>
          ) : (
            claims.slice(claimPage * PAGE_SIZE, (claimPage + 1) * PAGE_SIZE).map((claim) => (
              <TableRow key={claim.reviewId} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {claim.venueName || claim.venue?.name || 'Unknown Venue'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {claim.venue?.address || ''}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={claim.documentType?.replace('_', ' ').toUpperCase() || 'DOCUMENT'}
                    size="small"
                    color="primary"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{formatDate(claim.createdAt)}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={claim.status}
                    size="small"
                    color={
                      claim.status === 'approved'
                        ? 'success'
                        : claim.status === 'rejected'
                        ? 'error'
                        : 'warning'
                    }
                  />
                </TableCell>
                {showActions && (
                  <TableCell align="center">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Visibility />}
                      onClick={() => handleOpenReview(claim)}
                    >
                      Review
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
    </>
  );

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }} role="status" aria-live="polite">
        <CircularProgress size={48} aria-label="Loading" />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading claim reviews...</Typography>
      </Box>
    );
  }

  return (
    <Box role="main">
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Assignment sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Venue Claim Reviews</Typography>
        </Box>
        <Button startIcon={<Refresh />} onClick={loadClaimReviews} disabled={loading}>
          Refresh
        </Button>
      </Box>
      <TabDescription>
        Venue ownership claims submitted through the public claim flow, fetched from the admin review queue
        API. Review pending claims by checking the submitted verification documents and business details
        against the venue listing. Approve legitimate claims to grant the owner access to their venue
        dashboard, or reject with a reason. Previously reviewed claims are accessible under the Approved
        and Rejected sub-tabs.
      </TabDescription>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      )}

      <Card sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: 0 }}>
          <Tabs
            value={currentTab}
            onChange={(e, v) => { setCurrentTab(v); setClaimPage(0); }}
            sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}
          >
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Pending
                  {pendingClaims.length > 0 && (
                    <Chip label={pendingClaims.length} size="small" color="warning" />
                  )}
                </Box>
              }
            />
            <Tab label={`Approved (${approvedClaims.length})`} />
            <Tab label={`Rejected (${rejectedClaims.length})`} />
          </Tabs>

          <Box sx={{ p: 3 }}>
            {currentTab === 0 && <ClaimTable claims={pendingClaims} showActions />}
            {currentTab === 1 && <ClaimTable claims={approvedClaims} />}
            {currentTab === 2 && <ClaimTable claims={rejectedClaims} />}
          </Box>
        </CardContent>
      </Card>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onClose={handleCloseReview} maxWidth="md" fullWidth aria-labelledby="review-claim-dialog-title">
        <DialogTitle id="review-claim-dialog-title">
          <Typography variant="h5" sx={{ fontWeight: 600 }}>Review Venue Claim</Typography>
        </DialogTitle>
        <DialogContent>
          {selectedClaim && (
            <Box sx={{ pt: 2 }}>
              {/* Venue Information */}
              <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    <Business sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Venue Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">Venue Name</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {selectedClaim.venueName || selectedClaim.venue?.name || 'Unknown'}
                      </Typography>
                    </Grid>
                    {(selectedClaim.venue?.address) && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">
                          <LocationOn fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                          Address
                        </Typography>
                        <Typography variant="body2">{selectedClaim.venue.address}</Typography>
                      </Grid>
                    )}
                    {selectedClaim.venue?.phone && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          <Phone fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                          Phone
                        </Typography>
                        <Typography variant="body2">{selectedClaim.venue.phone}</Typography>
                      </Grid>
                    )}
                    {selectedClaim.venue?.website && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          <Language fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                          Website
                        </Typography>
                        <Typography variant="body2">
                          <a href={selectedClaim.venue.website} target="_blank" rel="noopener noreferrer">
                            {selectedClaim.venue.website}
                          </a>
                        </Typography>
                      </Grid>
                    )}
                    {selectedClaim.session?.userEmail && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          <Email fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                          Claimant Email
                        </Typography>
                        <Typography variant="body2">{selectedClaim.session.userEmail}</Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>

              {/* Document */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    <Description sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Verification Document
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">Document Type</Typography>
                    <Chip
                      label={selectedClaim.documentType?.replace('_', ' ').toUpperCase() || 'Unknown'}
                      color="primary"
                      sx={{ mt: 1 }}
                    />
                  </Box>

                  {selectedClaim.documentUrl && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Document
                      </Typography>
                      <Box
                        sx={{
                          border: 1,
                          borderColor: 'grey.300',
                          borderRadius: 1,
                          p: 2,
                          textAlign: 'center',
                          bgcolor: 'grey.50',
                        }}
                      >
                        {/\.(jpg|jpeg|png)$/i.test(selectedClaim.documentUrl) ? (
                          <img
                            src={selectedClaim.documentUrl}
                            alt="Verification document"
                            style={{ maxWidth: '100%', maxHeight: 400 }}
                          />
                        ) : (
                          <Box>
                            <Description sx={{ fontSize: 64, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              PDF Document
                            </Typography>
                            <Button
                              variant="outlined"
                              href={selectedClaim.documentUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              startIcon={<Visibility />}
                            >
                              Open Document
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Admin Notes */}
              <TextField
                fullWidth
                label="Review Notes (optional)"
                multiline
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes about your review decision..."
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Rejection Reason (required if rejecting)"
                multiline
                rows={2}
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Explain why this claim is being rejected..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button onClick={handleCloseReview} disabled={actionLoading}>Cancel</Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={actionLoading ? <CircularProgress size={16} /> : <Cancel />}
            onClick={handleRejectClaim}
            disabled={actionLoading || !rejectionReason.trim()}
          >
            Reject
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={actionLoading ? <CircularProgress size={16} /> : <CheckCircle />}
            onClick={handleApproveClaim}
            disabled={actionLoading}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminClaimReview;
